<template>
  <div>
    <v-card outlined class="mt-5">
      <v-list-item>
        <v-list-item-avatar>
          <v-avatar color="primary">
            <v-icon dark>{{
              participant.type == $capitalize($tc("model.damages_insured"))
                ? "mdi-lock"
                : "mdi-lock-open"
            }}</v-icon>
          </v-avatar>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>{{ participant.contact.name }}</v-list-item-title>
          <v-list-item-subtitle>{{ participant.type }}</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-divider></v-divider>
      <v-card-actions>
        <v-chip
          :color="has_spent_more ? 'red darken-1' : ''"
          :dark="has_spent_more"
        >
          <v-icon left>mdi-cash-usd</v-icon>
          {{ formatEuro(totalBudget) }}
        </v-chip>
        <v-chip class="ml-1" v-if="participant.damage_claims.length > 0">
          <v-icon left>
            {{ classificationResume.icon }}
          </v-icon>
          {{ classificationResume.description }}
        </v-chip>
        <v-spacer />
        <v-btn icon @click="timeline_opened = !timeline_opened">
          <v-icon>{{
            timeline_opened ? "mdi-chevron-up" : "mdi-chevron-down"
          }}</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-expand-transition>
      <v-timeline align-top dense v-if="timeline_opened">
        <v-timeline-item
          :color="has_spent_more ? 'red darken-1' : 'grey darken-1'"
          fill-dot
          v-for="claim in participant.damage_claims"
          :key="claim.id"
        >
          <DamageClaim
            :claim="claim"
            :classification_style="classification_style"
            :participant_type="participant.type"
            @calcBudget="calcBudget"
          />
        </v-timeline-item>
        <v-timeline-item
          v-if="participant.damage_claims.length == 0"
          color="grey darken-1"
          fill-dot
        >
          <empty-state
            :when="participant.damage_claims.length == 0"
            :message="$capitalize($tc('model.damages_no_damage'))"
            color="#f1f1f1"
          />
        </v-timeline-item>
        <v-timeline-item
          :color="has_spent_more ? 'red darken-1' : 'primary'"
          fill-dot
        >
          <v-btn
            @click="openNewDamage"
            :color="has_spent_more ? 'red darken-1' : 'primary'"
            outlined
            dark
            v-if="!open_new_damage"
          >
            <v-icon left>mdi-plus</v-icon>
            {{ $capitalize($tc("model.damages_register_damage")) }}
          </v-btn>

          <v-card v-else color="white">
            <v-form @submit.prevent="handleSubmitDamage">
              <ValidationObserver ref="form">
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" sm="3">
                      <ValidationProvider
                        name="damage type"
                        rules="required"
                        v-slot="{ errors }"
                      >
                        <v-select
                          name="damage_type"
                          :label="$capitalize($tc('model.damages_damage_type'))"
                          v-model="form.damage_type"
                          @change="form.damage = null"
                          :items="damage_types"
                          item-text="name"
                          outlined
                          dense
                          return-object
                          :error-messages="errors"
                        />
                      </ValidationProvider>
                    </v-col>

                    <v-col v-if="is_room" cols="12" sm="4">
                      <ValidationProvider
                        name="type"
                        :rules="is_room ? 'required' : ''"
                        v-slot="{ errors }"
                      >
                        <v-combobox
                          :label="
                            $capitalize($tc('model.damages_damaged_object'))
                          "
                          name="room_type"
                          v-model="form.room_feature.room_type"
                          :items="room_types"
                          item-text="name"
                          outlined
                          dense
                          return-object
                          :error-messages="errors"
                        >
                          <template v-slot:item="{ item }">
                            <v-icon small left>mdi-{{ item.icon }}</v-icon>
                            {{ item.name }}
                          </template>
                        </v-combobox>
                      </ValidationProvider>
                    </v-col>

                    <v-col v-if="is_product" cols="12" sm="4">
                      <v-btn
                        color="primary"
                        small
                        elevation="0"
                        :block="breakpoint == 'xs'"
                        class="ml-1 mt-1"
                        @click="product_dialog = true"
                      >
                        <v-icon left>{{
                          product_was_chosen
                            ? "mdi-square-edit-outline"
                            : "mdi-plus"
                        }}</v-icon>
                        {{
                          product_was_chosen
                            ? form.product_feature.local_category_name
                            : $capitalize($tc("model.damages_choose_product"))
                        }}
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="7">
                      <ValidationProvider
                        name="damage"
                        rules="required"
                        v-slot="{ errors }"
                      >
                        <v-combobox
                          name="damage"
                          :disabled="damages.length == 0"
                          v-model="form.damage"
                          :label="$capitalize($tc('model.damages_damage'))"
                          item-value="id"
                          item-text="name"
                          :items="damages"
                          dense
                          outlined
                          clearable
                          :error-messages="errors"
                        />
                      </ValidationProvider>
                    </v-col>
                  </v-row>
                  
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="closeNewDamage">{{
                    $capitalize($tc("model.damages_cancel_button"))
                  }}</v-btn>
                  <v-btn
                    color="primary"
                    outlined
                    :loading="submitting"
                    type="submit"
                  >
                    {{ $capitalize($tc("model.damages_save_button")) }}
                  </v-btn>
                </v-card-actions>
              </ValidationObserver>
            </v-form>
          </v-card>
        </v-timeline-item>
      </v-timeline>
    </v-expand-transition>
    <v-dialog
      v-model="product_dialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <ProductForm
        :product="form.product_feature"
        :product_dialog="product_dialog"
        @submit="setProduct"
        @cancel="product_dialog = false"
      />
    </v-dialog>
  </div>
</template>
s
<script>
import Money from "@/tools/money";
import { calcRoomBudget } from "@/services/budgets";
import {
  damage_classification,
  storeDamageClaim,
  damage_types
} from "@/services/damages";
import { calcTotalToSpend } from "@/services/policy_coverage";
import DamageClaim from "@/components/Lead/DamageClaim";
import EmptyState from "@/components/EmptyState";
import ProductForm from "@/components/Lead/ProductForm";

export default {
  name: "participant-damage-claim",
  components: {
    DamageClaim,
    EmptyState,
    ProductForm
  },
  props: {
    participant: {
      type: Object,
      default: () => ({
        contact: {},
        damage_claims: []
      })
    }
  },
  data: function() {
    return {
      timeline_opened: false,
      open_new_damage: false,
      product_dialog: false,
      budget_dialog: false,
      budget_claim: {},
      classification_style: {
        inconclusive: {
          description: this.$capitalize(
            this.$tc("model.damages_inconclusive_classification")
          ),
          icon: "mdi-help-circle",
          color: "yellow darken-1",
          dark: false
        },
        unsuitable: {
          description: this.$capitalize(
            this.$tc("model.damages_mismatched_classification")
          ),
          icon: "mdi-close-circle",
          color: "red lighten-1",
          dark: true
        },
        suitable: {
          description: this.$capitalize(
            this.$tc("model.damages_matched_classification")
          ),
          icon: "mdi-check-circle",
          color: "green lighten-1",
          dark: true
        },
        partially_suitable: {
          description: this.$capitalize(
            this.$tc("model.damages_partial_match_classification")
          ),
          icon: "mdi-minus-circle",
          color: "orange darken-1",
          dark: true
        }
      },
      form: {
        damage_type: {},
        room_feature: {
          room_type: null
        },
        product_feature: {
          local_category_id: null,
          local_category_name: "",
          brand_id: null,
          brand_name: ""
        },
        damage: null,
        classification: null,
        needs_research: false
      },
      submitting: false
    };
  },
  methods: {
    resetForm() {
      this.form = {
        damage_type: {},
        room_feature: {
          room_type: null
        },
        product_feature: {
          local_category_id: null,
          local_category_name: "",
          brand_id: null,
          brand_name: ""
        },
        damage: null,
        classification: null
      };
    },
    setProduct(product) {
      this.form.product_feature = product;
    },
    async handleSubmitDamage() {
      const validation = await this.$refs.form.validate();
      if (!validation) return;

      const damage = this.form.damage?.id
        ? this.form.damage
        : {
            id: null,
            name: this.form.damage
          };

      const room_type = this.form.room_feature.room_type?.id
        ? this.form.room_feature.room_type
        : {
            id: null,
            name: this.form.room_feature.room_type
          };

      const claim = {
        ...this.form,
        participant: this.participant,
        damage_id: damage.id,
        damage_name: damage.name,
        room_feature: {
          ...this.form.room_feature,
          room_type_id: room_type.id,
          room_type_name: room_type.name
        },
      };

      this.submitting = true;
      try {
        const response = await storeDamageClaim(
          this.$route.params["lead_number"],
          claim
        );
        this.resetForm();
        this.open_new_damage = false;
        this.$store.dispatch("damage_claim/addDamageClaim", response);
      } catch (e) {
        this.$refs.form.setErrors(e.response.data.errors);
        this.$store.commit("sendMessage", {
          text: `(${e.response.status}) ${e.response.data.message}`,
          color: "red"
        });
      } finally {
        this.submitting = false;
      }
    },
    handleDamageTypeChange(damage_type) {
      this.form.damage = null;
      this.form.damage_type = damage_type;
    },
    formatEuro(value) {
      return Money.euro(value ? value : 0);
    },
    calcBudget(claim) {
      if (claim.damage_type.id == damage_types.edificio)
        return claim.room_budgets
          .filter(budget => budget.selected)
          .reduce((sum, budget) => {
            return (sum += calcRoomBudget(budget));
          }, 0);
      else
        return claim.product_budgets
          .filter(budget => budget.selected)
          .reduce((sum, budget) => {
            return (sum += Number(budget.price));
          }, 0);
    },
    getClassificationClaim(claim) {
      if (claim.classification == damage_classification.suitable)
        return this.classification_style["suitable"];

      if (claim.classification == damage_classification.unsuitable)
        return this.classification_style["unsuitable"];

      if (claim.classification == damage_classification.inconclusive)
        return this.classification_style["inconclusive"];
    },
    has_coverage_surpassed_value(coverage) {
      const used_coverage =
        this.lead.used_coverages?.find(rec => rec.id == coverage.id) || {};

      return (
        Number(used_coverage.total_budget || 0) >
        calcTotalToSpend(used_coverage)
      );
    },
    openNewDamage() {
      const damage_type = this.damage_types.find(
        type => type.id == damage_types.conteudo
      );

      this.form.damage_type = damage_type ? damage_type : {};
      this.open_new_damage = true;
    },
    closeNewDamage() {
      this.open_new_damage = false;
      this.resetForm();
    }
  },
  computed: {
    product_was_chosen: function() {
      return !!this.form.product_feature.local_category_name;
    },
    is_inconclusive: function() {
      return this.form.classification == damage_classification.inconclusive;
    },
    is_room: function() {
      return this.form.damage_type.id == damage_types.edificio;
    },
    is_product: function() {
      return this.form.damage_type.id == damage_types.conteudo;
    },
    damage_types: function() {
      return this.$store.getters["damage_claim/getDamageTypes"];
    },
    damages: function() {
      if (this.form.damage_type?.id)
        return this.damage_types.find(
          damage_type => damage_type.id == this.form.damage_type?.id
        )?.damages;

      return [];
    },
    room_types: function() {
      return this.$store.getters["room_types/getRoomTypes"];
    },
    local_categories: function() {
      return this.$store.getters["local_categories/getLocalCategories"];
    },
    breakpoint: function() {
      return this.$vuetify.breakpoint.name;
    },
    totalBudget: function() {
      const total_budget = this.participant.damage_claims.reduce(
        (sum, curr) => {
          return (sum += this.calcBudget(curr));
        },
        0
      );

      return total_budget;
    },
    classificationResume: function() {
      const some_suitable = this.participant.damage_claims.some(
        claim => claim.classification == damage_classification.suitable
      );

      if (!some_suitable) return this.classification_style["unsuitable"];

      const some_unsuitable = this.participant.damage_claims.some(
        claim => claim.classification != damage_classification.suitable
      );

      if (!some_unsuitable) return this.classification_style["suitable"];

      if (some_unsuitable && some_suitable)
        return this.classification_style["partially_suitable"];

      return {};
    },
    classification_const: function() {
      return damage_classification;
    },
    has_spent_more: function() {
      return this.participant.damage_claims.some(claim => {
        if (claim.damage_type.id == damage_types.edificio)
          return claim.room_budgets.some(budget =>
            this.has_coverage_surpassed_value(budget.coverage)
          );
        else
          return claim.product_budgets.some(budget =>
            this.has_coverage_surpassed_value(budget.coverage)
          );
      });
    },
    lead: function() {
      return this.$store.getters.getLead;
    }
  }
};
</script>

<style></style>
